import React from "react";
import { View, StyleSheet } from "react-native";
import BaseExtensionController from "./BaseExtensionController";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtListRow from "../../components/NtListRow";
import NtListActionButton from "../../components/NtListActionButton";
import NtAudioPlayer from "../../components/NtAudioPlayer";
import {
  fetchRecordings,
  fetchRecordingAudio,
  uploadRecording,
  deleteRecording,
} from "../../api/recording";
import {
  asyncStorageSetData,
  showToast,
} from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import recordingcsvcolumndata from "../data/recordingcsvcolumndata";

class RecordingController extends BaseExtensionController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    page: 1,
  };

  column_location = "@recording_column_storage";

  componentDidMount() {
    super.componentDidMount?.();
    this.handleDefaultColumnsFetch(
      this.column_location,
      recordingcsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }


  handleDataFetch = async () => {
    const domain = this.context?.user?.domain;
    if (!domain) return;
    this.setState({ loading: true });
    const res = await fetchRecordings(domain, this.state.page, settings.pageSizeLoad);
    Logger("Recordings fetch response", res);
    if (res.ok) {
      this.setState({
        data: res.data?.data || [],
        totalItems: res.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const domain = this.context?.user?.domain;
    const res = await fetchRecordings(domain, 1, 10000000);
    return res.ok ? res.data?.data || [] : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "player") return "";
    return item[column];
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  handleUploadPress = () => {
    if (typeof document === "undefined") {
      Logger("Recording upload — mobile picker not implemented");
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "audio/wav,audio/mpeg,audio/mp3,audio/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const domain = this.context?.user?.domain;
      // Strip the extension so the display name is clean (the backend keeps
      // the original filename separately as `recording_filename`).
      const recordingName = file.name.replace(/\.[^/.]+$/, "");
      const form = new FormData();
      form.append("file", file);
      form.append("recording_name", recordingName);
      this.setState({ loading: true });
      const res = await uploadRecording(domain, form);
      Logger("Recording upload response", res);
      this.setState({ loading: false });
      if (res?.ok) {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", "Successfully uploaded the recording");
      } else {
        const msg =
          res?.data?.error ||
          res?.data?.message ||
          "Failed to upload the recording.";
        showToast("Upload failed", msg, "error");
      }
    };
    input.click();
  };

  handleDelete = async (item) => {
    const domain = this.context?.user?.domain;
    const id = item.id || item.recording_uuid;
    await deleteRecording(domain, id);
    this.setState({ page: 1 }, () => this.handleDataFetch());
    showToast("Success", "Successfully deleted the recording");
  };

  loadRecordingAudio = (item) => async () => {
    const domain = this.context?.user?.domain;
    const id = item.id || item.recording_uuid;
    const res = await fetchRecordingAudio(domain, id);
    Logger("Recording audio fetch response", res);
    if (!res.ok || !res.data) return null;
    return res.data; // Blob — NtAudioPlayer handles URL + waveform decode.
  };

  handleDownload = async (item) => {
    if (typeof document === "undefined") return;
    const domain = this.context?.user?.domain;
    const id = item.id || item.recording_uuid;
    const res = await fetchRecordingAudio(domain, id);
    Logger("Recording download response", res);
    if (!res?.ok || !res?.data) {
      showToast("Download failed", "Couldn't fetch the audio file.", "error");
      return;
    }
    const filename =
      item.recording_filename ||
      `${item.recording_name || "recording"}.wav`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  renderRow = (item, index) => (
    <NtListRow
      key={item.recording_uuid || item.id || index}
      item={item}
      columns={this.state.columns}
      renderCustom={(row, column) => {
        if (column === "player") {
          return <NtAudioPlayer loadAudio={this.loadRecordingAudio(row)} />;
        }
      }}
      renderLeadingAction={
        <NtListActionButton
          icon="download-outline"
          onPress={() => this.handleDownload(item)}
        />
      }
      onActionIcon="delete"
      onActionColor="red"
      onActionPress={
        this.handlePermissionCheck(settings.permissionTypeEdit)
          ? () => this.handleDelete(item)
          : null
      }
    />
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const count = this.state.totalItems || 0;
    const subtitle = `Audio files for IVR greetings, prompts, and announcements · ${count} file${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <NtPageActionButton
        icon="arrow-up"
        title="Upload Recording"
        variant="primary"
        onPress={this.handleUploadPress}
      />
    ) : null;

    return (
      <NtRootPageContainer
        pageTitle="Recordings"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: canEdit ? 35 : 0 }}
              columns={recordingcsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="recordings"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default RecordingController;
